import React, { useEffect, useRef } from 'react';
import { Coins, Loader2, Sparkles } from 'lucide-react';
import {
  DOUBLE_CREDIT_OFFER_COPY,
  IMAGEMOTION_DOUBLE_CREDIT_OFFERS,
  doubleCreditCtaLabel,
  formatMoney,
  type DoubleCreditOfferTier,
  type RehubCurrency,
} from '@rehub/pricing';
import { trackImagemotionEvent } from '../services/analytics';

type Currency = RehubCurrency;

interface DoubleCreditOfferProps {
  userId: string;
  currency: Currency;
  processingId: string | null;
  onSelect: (tier: DoubleCreditOfferTier) => void;
  variant?: 'panel' | 'inline' | 'completion';
  compact?: boolean;
}

function resolvePriceId(tier: DoubleCreditOfferTier, currency: Currency): string {
  const offer = IMAGEMOTION_DOUBLE_CREDIT_OFFERS[tier];
  const env = (import.meta as any).env || {};
  const key = offer.priceEnvKeys[currency];
  return (env[key] as string) || offer.defaultStripePriceIds?.[currency] || '';
}

export function getDoubleCreditPriceId(tier: DoubleCreditOfferTier, currency: Currency): string {
  return resolvePriceId(tier, currency);
}

const DoubleCreditOffer: React.FC<DoubleCreditOfferProps> = ({
  userId,
  currency,
  processingId,
  onSelect,
  variant = 'panel',
  compact = false,
}) => {
  const viewedRef = useRef(false);

  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    trackImagemotionEvent('double_credit_offer_viewed', userId, {
      offer_eligible: true,
      currency,
      source: variant,
    });
  }, [userId, currency, variant]);

  const headline =
    variant === 'completion'
      ? DOUBLE_CREDIT_OFFER_COPY.afterTesterHeadline
      : DOUBLE_CREDIT_OFFER_COPY.headline;
  const body =
    variant === 'completion'
      ? DOUBLE_CREDIT_OFFER_COPY.afterTesterBody
      : DOUBLE_CREDIT_OFFER_COPY.body;

  return (
    <section
      className={`rounded-2xl border border-amber-500/40 bg-gradient-to-br from-amber-950/40 via-slate-900 to-slate-900 ${
        compact ? 'p-4' : 'p-5 sm:p-6'
      }`}
      aria-labelledby="double-credit-offer-heading"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5 text-amber-300" />
        </div>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-400 mb-1">
            One-time offer
          </p>
          <h3 id="double-credit-offer-heading" className="text-lg sm:text-xl font-bold text-white">
            {headline}
          </h3>
          <p className="text-sm text-slate-300 mt-1 leading-relaxed">{body}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {(Object.keys(IMAGEMOTION_DOUBLE_CREDIT_OFFERS) as DoubleCreditOfferTier[]).map((tier) => {
          const offer = IMAGEMOTION_DOUBLE_CREDIT_OFFERS[tier];
          const priceId = resolvePriceId(tier, currency);
          const configured = Boolean(priceId);
          const isProcessing = processingId === `double_${tier}`;
          const amount = offer.prices[currency];

          return (
            <div
              key={tier}
              className={`relative flex flex-col rounded-xl border-2 p-4 ${
                offer.bestValue
                  ? 'border-amber-400 bg-amber-500/10'
                  : 'border-slate-700 bg-slate-800/60'
              }`}
            >
              {offer.bestValue && (
                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-amber-400 text-black text-[10px] font-bold rounded-full uppercase tracking-wide">
                  Best Value
                </span>
              )}
              <p className="text-xs font-medium text-slate-400">{offer.name}</p>
              <p className="mt-1 text-4xl font-bold text-white leading-none">
                {offer.totalCredits}
                <span className="text-base font-semibold text-slate-300 ml-1">credits</span>
              </p>
              <p className="mt-2 text-sm text-slate-300 flex items-center gap-1.5">
                <Coins className="w-3.5 h-3.5 text-amber-300" />
                Buy {offer.purchasedCredits} + get {offer.bonusCredits} bonus
              </p>
              <p className="text-lg font-bold text-white mt-2">
                {formatMoney(amount, currency)}
                <span className="text-xs font-medium text-slate-400 ml-1">one-time · no subscription</span>
              </p>
              <button
                type="button"
                onClick={() => {
                  trackImagemotionEvent('double_credit_offer_selected', userId, {
                    offer_tier: tier,
                    currency,
                    offer_eligible: true,
                  });
                  onSelect(tier);
                }}
                disabled={!!processingId || !configured}
                className={`mt-4 w-full py-2.5 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 ${
                  offer.bestValue
                    ? 'bg-amber-400 hover:bg-amber-300 text-black'
                    : 'bg-slate-100 hover:bg-white text-slate-900'
                }`}
              >
                {isProcessing ? (
                  <span className="inline-flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading...
                  </span>
                ) : !configured ? (
                  'Offer unavailable'
                ) : (
                  doubleCreditCtaLabel(tier, currency)
                )}
              </button>
              {!configured && (
                <p className="mt-2 text-[11px] text-slate-500">
                  Stripe price not configured for {currency}.
                </p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default DoubleCreditOffer;
